import React from "react";
import { Link } from "react-router-dom";

const CopiedLabelsPage: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold" style={{ color: "#0A2A4A" }}>
            Pureaum
          </Link>
          <Link to="/" className="text-sm font-semibold hover:underline" style={{ color: "#0A2A4A" }}>
            Back to homepage
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-10">
        <article className="space-y-6">
          <header className="space-y-2">
            <h1 className="text-3xl font-bold" style={{ color: "#0A2A4A" }}>
              Why copied labels break most anti-counterfeit systems
            </h1>
            <p className="text-sm" style={{ color: "#2B2B2B" }}>
              Static identifiers can be duplicated. Effective prevention requires understanding how products behave
              after they enter the market.
            </p>
          </header>

          <section className="space-y-3">
            <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
              The core problem: static identifiers are not security
            </h2>
            <ul className="list-disc list-inside space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
              <li>Any printed code can be copied or reprinted.</li>
              <li>Once packaging leaves the factory, labels can be harvested and reused.</li>
              <li>Static IDs prove existence of a record, not authenticity of the item in hand.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
              Why verification pages alone are not enough
            </h2>
            <ul className="list-disc list-inside space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
              <li>Consumers see a “valid” page even if a label was copied dozens of times.</li>
              <li>No signal about where or how often the same code is being scanned.</li>
              <li>Issues are discovered only after complaints and brand damage.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
              The missing ingredient: market behavior intelligence
            </h2>
            <ul className="list-disc list-inside space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
              <li>Detect unusual scan patterns that do not fit normal sell-through.</li>
              <li>Spot geo-velocity jumps and clustering around specific outlets.</li>
              <li>Use post-consumption signals to identify harvested labels.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
              The practical solution: detection and containment, not just validation
            </h2>
            <ul className="list-disc list-inside space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
              <li>Continuously monitor scan behavior in the market.</li>
              <li>Alert and contain when risk appears, instead of waiting for complaints.</li>
              <li>Strengthen verification only when signals warrant it.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
              How Pureaum addresses copied labels
            </h2>
            <ul className="list-disc list-inside space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
              <li>Cloud Scan Intelligence as the primary control to detect duplicate scans, geo anomalies, and clusters.</li>
              <li>Outer QR as the default public verification layer that feeds telemetry.</li>
              <li>Optional Inner QR as the second line of defense plus loyalty engine for premium SKUs or high-risk regions.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
              Bottom line
            </h2>
            <p className="text-sm" style={{ color: "#2B2B2B" }}>
              With Outer QR plus Cloud Scan Intelligence you get early detection and faster containment. With optional
              Inner QR enabled, brands can operate 90%+ counterfeit-safe in practice. This is a program-level outcome
              when deployed correctly; no absolute guarantees are claimed.
            </p>
          </section>

          <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 overflow-x-auto">
              <h3 className="text-lg font-bold mb-3" style={{ color: "#0A2A4A" }}>
              Copied label flow and detection
            </h3>
            <svg
              width="100%"
              height="240"
              viewBox="0 0 900 240"
              role="img"
              aria-label="Copied label flow and cloud detection"
            >
              <defs>
                <style>
                  {`.node{fill:#fff;stroke:#D6E8FB;stroke-width:2;}
                    .label{font:700 12px 'Inter', system-ui; fill:#0A2A4A;}
                    .muted{font:600 11px 'Inter', system-ui; fill:#2B2B2B;}
                    .arrow{stroke:#BFD3EA;stroke-width:3;stroke-linecap:round;fill:none;}
                    .alert{stroke:#FF7A29;stroke-width:3;stroke-linecap:round;fill:none;}
                    .cloud{fill:#EAF2FF;stroke:#BFD3EA;stroke-width:2;}
                    .pill{fill:#FFF4EC;stroke:#FF7A29;stroke-width:1.5;}
                    .pilltext{font:700 10px 'Inter', system-ui; fill:#FF7A29;}`}
                </style>
                <marker id="arrowHeadBlue" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#BFD3EA" />
                </marker>
                <marker id="arrowHeadOrange" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#FF7A29" />
                </marker>
              </defs>
              <g transform="translate(40,95)">
                <rect className="node" x="0" y="0" width="140" height="60" rx="10" />
                <text className="label" x="12" y="24">Label issued</text>
                <text className="muted" x="12" y="44">Producer</text>
              </g>
              <g transform="translate(230,95)">
                <rect className="node" x="0" y="0" width="140" height="60" rx="10" />
                <text className="label" x="12" y="24">Copied label</text>
                <text className="muted" x="12" y="44">Counterfeiter</text>
              </g>
              <g transform="translate(430,55)">
                <rect className="cloud" x="0" y="0" width="220" height="110" rx="20" />
                <text className="label" x="16" y="28">Cloud Scan Intelligence</text>
                <text className="muted" x="16" y="52">Duplicate scans flagged</text>
                <text className="muted" x="16" y="72">Geo anomalies and clusters</text>
                <text className="muted" x="16" y="92">Suspicious retailer hot-spots</text>
              </g>
              <g transform="translate(710,95)">
                <rect className="node" x="0" y="0" width="140" height="60" rx="10" />
                <text className="label" x="12" y="24">Containment</text>
                <text className="muted" x="12" y="44">Alerts to brand</text>
              </g>

              <line className="arrow" x1="180" y1="125" x2="230" y2="125" markerEnd="url(#arrowHeadBlue)" />
              <line className="arrow" x1="370" y1="125" x2="430" y2="125" markerEnd="url(#arrowHeadBlue)" />
              <line className="alert" x1="650" y1="125" x2="710" y2="125" markerEnd="url(#arrowHeadOrange)" />

              <rect className="pill" x="395" y="185" width="230" height="32" rx="12" />
              <text className="pilltext" x="405" y="205">Alerts with affected IDs and batches</text>
            </svg>
          </section>
        </article>
      </main>
    </div>
  );
};

export default CopiedLabelsPage;
